import { z } from "zod";
import { higherPlan, type Plan } from "./enums";

/**
 * Billing contract shared by the API and the web app.
 *
 * Paid tiers on the web are sold through a merchant of record (Paddle): they are
 * the seller, collect VAT and issue the receipt; we receive a webhook and grant
 * an *entitlement*. `user.plan` is derived from the entitlements table and
 * nothing else — a client never tells the server what it paid for.
 *
 * The same table is the merge point for Google Play purchases from the Android app
 * (`source: "google"`, dedupe on the purchase token — see google-play.ts), for App Store purchases from the Apple
 * apps (`source: "apple"`, dedupe on the original transaction id — see app-store.ts) and for Business contracts sold
 * by invoice (`source: "manual"`, written by the operator, no provider involved). The plan belongs to the account,
 * whichever store sold it: docs/research/2026-10-one-account-plan.md §4.
 */

/**
 * What the web sells: subscriptions only. Hosting, storage and email are running
 * costs, so a one-time payment is deliberately not offered here (the iOS
 * non-consumable is Apple's business and will be honoured via `kind: "lifetime"`
 * once purchases sync).
 *
 *  - premium_*: one couple, the whole wedding (3 active weddings).
 *  - pro_*:     professional planners (15 active weddings, +packs, templates, hand-off, planner dashboard).
 *  - business_annual: firms, venues, agencies — sold assisted, by invoice; never self-serve checkout.
 */
export const BILLING_PLAN_CODE = ["premium_monthly", "premium_annual", "pro_monthly", "pro_annual", "business_annual"] as const;
export type BillingPlanCode = (typeof BILLING_PLAN_CODE)[number];

/** Codes a signed-in customer may order or switch to on their own (on the web: Premium and Pro). */
export const PURCHASABLE_PLAN_CODE = ["premium_monthly", "premium_annual", "pro_monthly", "pro_annual"] as const;
export type PurchasablePlanCode = (typeof PURCHASABLE_PLAN_CODE)[number];

/**
 * What the App Store and Google Play sell (owner, 2026-11-03): Premium only, monthly and annual, at the web's
 * Premium list price (`LIST_PRICES_USD.premium_*`). Pro, its capacity packs and Business are sold on the web only.
 * A store plan bought before (a Google Play Pro subscription) keeps its tier: the server still recognises every
 * `BILLING_PLAN_CODE`; this list only says what a store may offer for sale.
 */
export const IN_APP_PLAN_CODE = ["premium_monthly", "premium_annual"] as const satisfies readonly PurchasablePlanCode[];
export type InAppPlanCode = (typeof IN_APP_PLAN_CODE)[number];

export function isInAppPlanCode(v: unknown): v is InAppPlanCode {
  return typeof v === "string" && (IN_APP_PLAN_CODE as readonly string[]).includes(v);
}

/**
 * Quantity items that ride on a Pro subscription. Paddle requires every item on a
 * subscription to share its billing cycle, so the capacity pack exists once per
 * cycle: the monthly pack for monthly Pro, the annual pack for annual Pro.
 */
export const BILLING_ADDON_CODE = ["pro_weddings_10", "pro_weddings_10_annual"] as const;
export type BillingAddonCode = (typeof BILLING_ADDON_CODE)[number];

export const INTERVAL_FOR_ADDON: Readonly<Record<BillingAddonCode, "month" | "year">> = {
  pro_weddings_10: "month",
  pro_weddings_10_annual: "year",
};

/** The capacity-pack price that may sit on a subscription with this billing interval. */
export function packCodeForInterval(interval: "month" | "year"): BillingAddonCode {
  return interval === "year" ? "pro_weddings_10_annual" : "pro_weddings_10";
}

/** Every catalog price the provider may be configured with. */
export type BillingPriceCode = BillingPlanCode | BillingAddonCode;
export const BILLING_PRICE_CODE: readonly BillingPriceCode[] = [...BILLING_PLAN_CODE, ...BILLING_ADDON_CODE];

export const PLAN_FOR_CODE: Readonly<Record<BillingPlanCode, Plan>> = {
  premium_monthly: "premium",
  premium_annual: "premium",
  pro_monthly: "pro",
  pro_annual: "pro",
  business_annual: "enterprise",
};

export const INTERVAL_FOR_CODE: Readonly<Record<BillingPlanCode, "month" | "year">> = {
  premium_monthly: "month",
  premium_annual: "year",
  pro_monthly: "month",
  pro_annual: "year",
  business_annual: "year",
};

/**
 * Published USD list prices, one per self-serve price. Paddle's localized,
 * tax-inclusive price is what checkout charges and what the pricing page shows
 * whenever billing is on; these are the fallback the public pricing page shows
 * while billing is off (or for a tier not yet in the catalog), and the amounts
 * the Paddle catalog is created with (tools/web/paddle-live-sync.mjs). Change
 * them here AND in Paddle (a new price per amount) — never in a component.
 * Premium is the same price on every store (`IN_APP_PLAN_CODE`).
 *
 * 2026-11-03 price cut (owner): sales dropped after Premium went from $4.99 to
 * $14.99/month, so Premium is back at $4.99/$49.99 and Pro, the capacity pack and
 * Business are lowered with it. Existing subscribers move to these prices at their
 * next renewal (tools/web/paddle-live-migrate.mjs). Annual ≈ 17% below twelve
 * monthly payments for both tiers.
 */
export const LIST_PRICES_USD: Readonly<Record<PurchasablePlanCode | BillingAddonCode, number>> = {
  premium_monthly: 4.99,
  premium_annual: 49.99,
  pro_monthly: 14.99,
  pro_annual: 149.99,
  pro_weddings_10: 5,
  pro_weddings_10_annual: 60,
};

/** "$149.99" / "$10" — en-US formatting for a USD list price (whole dollars drop the cents). */
export function formatUsd(amount: number): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: Number.isInteger(amount) ? 0 : 2, maximumFractionDigits: 2 }).format(amount);
}

/**
 * Whole-percent saving of a tier's annual price against twelve monthly payments,
 * from the published list prices (the Paddle catalog mirrors them, so the ratio
 * holds for every localized price). Premium and Pro both come out at 17%.
 */
export function annualSavingsPercent(tier: "premium" | "pro"): number {
  const monthly = LIST_PRICES_USD[`${tier}_monthly`];
  const annual = LIST_PRICES_USD[`${tier}_annual`];
  return Math.round((1 - annual / (12 * monthly)) * 100);
}

export function isPlanCode(v: unknown): v is BillingPlanCode {
  return typeof v === "string" && (BILLING_PLAN_CODE as readonly string[]).includes(v);
}

/**
 * Plans no subscription carries, both Premium for life and both App Store only: the $99 one-time unlock
 * (`premium_lifetime`) and the installs from before the first paid release that Apple kept free
 * (`premium_grandfathered`). Recorded as `kind: "lifetime"`; never sold by the web or Google Play.
 */
export const LIFETIME_PLAN_CODE = ["premium_lifetime", "premium_grandfathered"] as const;
export type LifetimePlanCode = (typeof LIFETIME_PLAN_CODE)[number];

export function isLifetimePlanCode(v: unknown): v is LifetimePlanCode {
  return typeof v === "string" && (LIFETIME_PLAN_CODE as readonly string[]).includes(v);
}
export function isAddonCode(v: unknown): v is BillingAddonCode {
  return typeof v === "string" && (BILLING_ADDON_CODE as readonly string[]).includes(v);
}

/** Tier granted by a plan code; a price we do not recognise still grants Premium (fail open only upward from free, never above). */
export function tierOfCode(code: string | null | undefined): Plan {
  return isPlanCode(code) ? PLAN_FOR_CODE[code] : "premium";
}

export const ENTITLEMENT_SOURCE = ["paddle", "mock", "apple", "google", "manual"] as const;
export type EntitlementSource = (typeof ENTITLEMENT_SOURCE)[number];

/** `lifetime` is never created by the web flow; reserved for App Store non-consumables (`source: "apple"`). */
export const ENTITLEMENT_KIND = ["subscription", "lifetime"] as const;
export type EntitlementKind = (typeof ENTITLEMENT_KIND)[number];

/**
 * Lifecycle of one purchase. Subscriptions move between the first five (the
 * provider's own states); `expired` is set by our nightly reconcile when a
 * period ended and no renewal arrived; `refunded` ends a subscription whose first
 * payment was refunded (right of withdrawal) or charged back.
 */
export const ENTITLEMENT_STATUS = ["active", "trialing", "past_due", "paused", "canceled", "expired", "refunded"] as const;
export type EntitlementStatus = (typeof ENTITLEMENT_STATUS)[number];

export const BILLING = {
  /**
   * Right of withdrawal for consumers (angrerettloven § 21; Consumer Rights
   * Directive art. 9): 14 days from purchase, full refund, no questions. We
   * refund the whole amount even though the law would allow charging for the
   * days used (§ 26) — simpler for everyone. Extended to Pro (business) buyers
   * as a courtesy. Quoted in the Terms and enforced by BillingService.withdraw.
   */
  withdrawalDays: 14,
  /**
   * Premium and Pro both start with a free trial, configured on each Paddle price
   * (the `trialing` status flows through the same webhook; the capacity pack has
   * none). Quoted in the Terms and on the pricing page; must match the catalog.
   */
  trialDays: 7,
  /**
   * Business is sold assisted (contact, call, invoice through the merchant of
   * record) and provisioned by hand. This is the published starting price, USD
   * per month billed annually — an anchor on the pricing page, not a Paddle price
   * (lowered from 149 with the 2026-11-03 price cut).
   */
  businessFromUsdPerMonth: 49,
  /**
   * A subscription whose current period ended this many days ago without a
   * renewal event is re-fetched from the provider by the nightly reconcile and
   * marked `expired` if the provider agrees. Covers a lost webhook.
   */
  reconcileAfterDays: 1,
  /** Provider webhooks older than this (per their signed timestamp) are rejected as replays. */
  webhookToleranceSeconds: 300,
} as const;

/** Statuses that grant access. `past_due` keeps access while the provider retries the card (dunning). */
export const ENTITLED_STATUSES: readonly EntitlementStatus[] = ["active", "trialing", "past_due"];

export interface EntitlementLike {
  kind: EntitlementKind;
  status: EntitlementStatus;
  /** Plan code of the base price; missing/unknown → premium. */
  planCode?: string | null;
  /** Pro capacity packs on this subscription. */
  packQuantity?: number | null;
}

export function isEntitled(e: EntitlementLike): boolean {
  if (e.kind === "lifetime") return e.status === "active";
  return ENTITLED_STATUSES.includes(e.status);
}

/** The one rule that turns purchases into `user.plan`: the highest entitled tier wins, none → free. */
export function planFromEntitlements(list: readonly EntitlementLike[]): Plan {
  let plan: Plan = "free";
  for (const e of list) if (isEntitled(e)) plan = higherPlan(plan, tierOfCode(e.planCode));
  return plan;
}

/** Packs only count on an entitled Pro subscription. */
export function packsFromEntitlements(list: readonly EntitlementLike[]): number {
  let n = 0;
  for (const e of list) if (isEntitled(e) && tierOfCode(e.planCode) === "pro") n += Math.max(0, e.packQuantity ?? 0);
  return n;
}

/* ───────────────────────── One plan, every store ───────────────────────── */

/** Where a plan can be bought or is billed: this site (Paddle), Google Play, the App Store, or an invoice. */
export const PURCHASE_CHANNEL = ["web", "google", "apple", "manual"] as const;
export type PurchaseChannel = (typeof PURCHASE_CHANNEL)[number];

/** The channel an entitlement row was bought through. The local mock stands in for the web's checkout. */
export function channelOfSource(source: EntitlementSource | string): PurchaseChannel {
  if (source === "google" || source === "apple" || source === "manual") return source;
  return "web";
}

/**
 * Whether the account pays twice for one plan: two entitled subscriptions that will both renew, or a renewing
 * subscription next to a lifetime unlock. A subscription already set to end (`cancelAt`) does not count.
 */
export function isBilledTwice(list: readonly (EntitlementLike & { cancelAt?: Date | string | null })[]): boolean {
  const live = list.filter(isEntitled);
  const renewing = live.filter((e) => e.kind === "subscription" && !e.cancelAt).length;
  const lifetime = live.some((e) => e.kind === "lifetime");
  return renewing >= 2 || (renewing >= 1 && lifetime);
}

/**
 * What a client on `here` may offer, the same answer on every platform:
 * - `buy`: a free account — this platform may sell a plan (if its own store is available);
 * - `manage`: the plan is billed by this platform's own store — manage, change or cancel it here;
 * - `elsewhere`: the plan is billed by another store or by invoice — say where, sell nothing (Apple 3.1.3(b) and
 *   Google Play allow honouring it; neither allows pointing to the other store's checkout);
 * - `included`: a lifetime unlock or a complimentary plan — nothing to buy, nothing to manage.
 */
export type PurchaseOption = { kind: "buy" } | { kind: "manage" } | { kind: "elsewhere"; channel: PurchaseChannel } | { kind: "included" };

export function purchaseOption(status: Pick<BillingStatusDTO, "plan" | "store" | "entitlement">, here: Exclude<PurchaseChannel, "manual">): PurchaseOption {
  if (status.plan === "free") return { kind: "buy" };
  const e = status.entitlement;
  if (!e || !status.store || e.kind === "lifetime") return { kind: "included" };
  if (status.store === here) return { kind: "manage" };
  return { kind: "elsewhere", channel: status.store };
}

/* ───────────────────────── Wire types ───────────────────────── */

export interface EntitlementDTO {
  id: string;
  source: EntitlementSource;
  kind: EntitlementKind;
  planCode: BillingPlanCode | LifetimePlanCode | "unknown";
  /** Tier this row grants while entitled. */
  tier: Plan;
  status: EntitlementStatus;
  packQuantity: number;
  /** When the customer first paid — the right-of-withdrawal clock starts here. */
  purchasedAt: string | null;
  currentPeriodEnd: string | null;
  /** Set when the customer cancelled and keeps access until this date. */
  cancelAt: string | null;
  canceledAt: string | null;
  updatedAt: string;
}

/** `GET /me/billing` */
export interface BillingStatusDTO {
  /** False when no payment provider is configured — the UI then shows nothing purchasable. */
  enabled: boolean;
  provider: "paddle" | "mock" | null;
  plan: Plan;
  /**
   * "subscription" = a provider subscription; "manual" = a Business contract written by the operator;
   * "complimentary" = a paid tier set by hand with no entitlement row; "lifetime" = the App Store's one-time unlock or
   * the grandfathered cohort.
   */
  source: "none" | "subscription" | "manual" | "lifetime" | "complimentary";
  /**
   * Where the current plan is billed — the only place it can be changed or cancelled. `web` = this site's checkout
   * (Paddle), `google` = Google Play, `apple` = the App Store, `manual` = an invoice. Null for a free or complimentary
   * account. Every client words it the same way through `purchaseOption`.
   */
  store: PurchaseChannel | null;
  /**
   * Two purchases would renew for this account (bought on two stores, say), or one renews next to a lifetime unlock:
   * the person is paying twice for one plan. The clients say so and point to where each one is billed.
   */
  billedTwice: boolean;
  entitlement: EntitlementDTO | null;
  /** Pro capacity packs currently on the account. */
  packs: number;
  /** Active weddings the account may own right now (tier + packs); not enforced while `activeWeddingsUnlimited`. */
  activeWeddingLimit: number;
  /** No active-wedding cap: the account holds an entitled App Store purchase (`activeWeddingCeiling`). Say "unlimited". */
  activeWeddingsUnlimited: boolean;
  /** What the customer may do right now — the server decides, the UI only shows. */
  can: {
    checkout: boolean;
    /**
     * Switch the base price of a live provider subscription: Premium ⇄ Pro at the same billing
     * interval. Never during a free trial (Paddle forbids item changes then) and never across
     * intervals (a subscription's billing cycle is fixed; cancel and start the other plan).
     */
    changePlan: boolean;
    /** Add or remove Pro capacity packs (same rules as changePlan, Pro only). */
    packs: boolean;
    cancel: boolean;
    resume: boolean;
    withdraw: boolean;
    portal: boolean;
  };
  withdrawalDeadline: string | null;
}

/** One purchasable price, priced by the provider for the visitor's country. */
export interface BillingPlanDTO {
  code: BillingPriceCode;
  /** Tier the price grants (or belongs to, for add-ons). */
  tier: Plan;
  kind: "plan" | "addon";
  interval: "month" | "year";
  /** Localized, provider-formatted amounts (what checkout will show). */
  formattedTotal: string;
  formattedTax: string | null;
  currency: string;
  /** True when `formattedTotal` includes VAT/sales tax for the visitor's country. */
  taxIncluded: boolean;
  /** Days of free trial configured on the price (0 = none). */
  trialDays: number;
}

/**
 * `GET /billing/plans` — public (no session): what the web sells, priced for the
 * visitor's country. `enabled: false` is a normal answer, not an error, so the
 * pricing page can say "not available yet" while billing is switched off.
 */
export interface BillingCatalogDTO {
  enabled: boolean;
  provider: "paddle" | "mock" | null;
  plans: BillingPlanDTO[];
}

/** `POST /me/billing/checkout` — the buyer's express consent is part of the order (CRD art. 16(m), angrerettloven § 22 n). */
export const startCheckoutSchema = z.object({
  plan: z.enum(PURCHASABLE_PLAN_CODE),
  /** "Start now; I know this waives nothing — I still have 14 days to withdraw for a full refund." */
  consent: z.literal(true),
});
export type StartCheckout = z.infer<typeof startCheckoutSchema>;

/** `POST /me/billing/plan` — change the base price of a running subscription (prorated by the provider). */
export const changePlanSchema = z.object({ plan: z.enum(PURCHASABLE_PLAN_CODE) });
export type ChangePlan = z.infer<typeof changePlanSchema>;

/** `POST /me/billing/packs` — set the number of Pro capacity packs (0 removes them). */
export const setPacksSchema = z.object({ quantity: z.number().int().min(0).max(20) });
export type SetPacks = z.infer<typeof setPacksSchema>;

export interface CheckoutSessionDTO {
  /** Where to send the browser: the dedicated checkout page (pay.bridewell.io, where Paddle's checkout renders) or the dev mock. */
  url: string;
}

export interface PortalSessionDTO {
  overview: string;
  updatePaymentMethod: string | null;
}
